import { Component, OnInit, OnDestroy, ViewChild, ElementRef, TemplateRef, inject } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { NbDialogRef, NbDialogService } from '@nebular/theme';
import { FolderService, EmailFolder } from 'src/app/deebr/service/folder.service';
import { Email, EmailStore, Template } from 'src/app/deebr/component/pages/generator/email.store';
import { GeneratorService } from 'src/app/deebr/service/generator.service';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, map, Observable } from 'rxjs';
import { ToastService, Toast } from 'src/app/deebr/service/toast.service';
import { TemplateModalComponent } from 'src/app/deebr/component/template-modal/template-modal.component';
import { Enterprise, UserStore } from 'src/app/deebr/store/user.store';

interface Sorting {
  key: string | null;
  direction: 'asc' | 'desc' | null;
}

@Component({
    selector: 'app-folder-view',
    templateUrl: './folder-view.component.html',
    styleUrls: ['./folder-view.component.scss'],
    standalone: false
})
export class FolderViewComponent implements OnInit, OnDestroy {
  private readonly userStore = inject(UserStore);
  @ViewChild('folderDialog') folderDialog!: TemplateRef<any>;
  @ViewChild('deleteFolderDialog') deleteFolderDialog!: TemplateRef<any>;
  @ViewChild('languageDialog') languageDialog!: TemplateRef<any>;
  @ViewChild('deleteEmailDialog') deleteEmailDialog!: TemplateRef<any>;
  @ViewChild('dialogErrorDeletingFolder') dialogErrorDeletingFolder!: TemplateRef<any>;
  @ViewChild('dialogSuccessDeletingFolder') dialogSuccessDeletingFolder!: TemplateRef<any>;
  @ViewChild('templateModal') templateModal!: TemplateModalComponent;
  private loadingToast?: Toast;
  
  folderId: number | 'root' = 'root';
  currentFolder: any = null;
  public folders: EmailFolder[] = [];
  public filteredFolders: EmailFolder[] = [];
  emails: Email[] = [];
  filteredEmails: Email[] = [];
  breadcrumb: any[] = [];
  loading: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';
  allFolders: EmailFolder[] = [];

  invalidDestinationIds: number[] = [];
  loadingPreviews: Set<number> = new Set<number>();
  
  // Email menu options
  showMenu: boolean = false;
  currentEmail!: any;
  
  // Folder options
  newFolderName: string = '';
  selectedFolder: EmailFolder | null = null;
  destinationFolderId: number | null = null;
  showFolderMenu: boolean = false;
  currentFolderMenu: EmailFolder | null = null;
  
  // Display options
  displayMode: string = 'grid';
  newEmailspinner: boolean = false;
  searchTerm: string = '';
  private searchTerm$ = new BehaviorSubject<string>('');

  // Global search (emails matching the term outside the current folder)
  otherEmails: Email[] = [];
  allEmailsCache: Email[] | null = null;
  loadingGlobalSearch = false;
  private folderNameMap = new Map<number, string>();
  private folderPathMap = new Map<number, string>();
  private folderMapLoaded = false;
  private globalSearchVersion = 0;

  // Template modal
  public templates: Template[] = [];
  public selectedTemplateId: number | null = null;
  public selectedTemplateName: string | null = null;
  
  // Sorting
  public sorting$: BehaviorSubject<Sorting> = new BehaviorSubject({
    key: 'heure',
    direction: 'desc',
  } as Sorting);
  
  // For translations
  languageList: Array<{ code: string; text: string }> = [];
  @ViewChild('dialogChoice') dialogChoiceTemplate!: TemplateRef<any>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private folderService: FolderService,
    private generatorService: GeneratorService,
    private dialogService: NbDialogService,
    private emailStore: EmailStore,
    private toastService: ToastService,
    private location: Location
  ) {
    this.generatorService.getTemplates().subscribe(t => this.templates = t);
  }

  ngOnInit() {
    // Get display mode from session storage
    const savedDisplayMode = sessionStorage.getItem('displayMode');
    if (savedDisplayMode) {
      this.displayMode = savedDisplayMode;
    }
    
    // Setup document listener for menu closing
    document.addEventListener('click', this.closeMenu.bind(this));

    // Get folder ID from route params
    this.route.paramMap.subscribe(params => {
      const id = params.get('folder_id');
      this.folderId = id === 'root' ? 'root' : Number(id);
      this.loadFolderContents();
    });
    
    // Setup search term subscription
    this.searchTerm$.subscribe(term => {
      this.filterEmails();
      this.filterFolders();
    });

    // Global search is debounced: it may trigger a full emails load
    this.searchTerm$.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => this.performGlobalSearch());
  }
  
  ngOnDestroy() {
    document.removeEventListener('click', this.closeMenu.bind(this));
  }

  openModal() {
    this.templateModal.open();
  }

  onTemplateSelect(id: number, name: string, type: string) {
    this.selectedTemplateId = id;
    this.selectedTemplateName = name;

    if (type === 'stripo_import') {
      this.newEmailspinner = true;
      this.emailStore.resetEmails();
      this.loadFolderContents();
      this.newEmailspinner = false;
      return;
    }

    this.createEmailFromTemplate(id, name, type);
  }

  createEmailFromTemplate(templateId: number, name: string, type: string) {
    this.newEmailspinner = true;
    const currentFolderId = this.folderId === 'root' ? null : this.folderId as number;

    this.generatorService.createEmailFromTemplate(templateId, name, type, currentFolderId).subscribe({
      next: ({ message, id }) => {
        // Rafraîchissez la liste des emails
        this.emailStore.resetEmails();
        this.loadFolderContents();
      },
      error: err => console.error('Erreur création email :', err),
      complete: () => this.newEmailspinner = false,
    });
  }

  loadFolderContents() {
    this.folderService.getFolderContents(this.folderId).subscribe({
      next: (data) => {
        this.currentFolder = data.current_folder;
        this.folders = data.folders;
        this.filteredFolders = [...data.folders];
        this.emails = data.emails;
        this.filterEmails();
        this.filterFolders();
        this.breadcrumb = data.breadcrumb;
        this.loading = false;

        // Contents changed (move, copy, rename...): global search data is stale
        this.allEmailsCache = null;
        this.folderMapLoaded = false;
        this.globalSearchVersion++;
        this.loadingGlobalSearch = false;
        this.performGlobalSearch();

        this.folders.forEach(folder => {
          this.folderService.getFolderContents(folder.id).subscribe(childData => {
            folder.email_count = childData.emails.length + childData.folders.length;
          });
        });
        

        
        
        // Smart Check:
        // 1. Missing previews (!preview_img_url)
        // 2. Modified since last check (modified_at > lastCheckTime)
        const lastCheckTime = parseInt(localStorage.getItem('last_preview_check_time') || '0', 10);
        
        console.log('FolderView checking previews. Last check:', new Date(lastCheckTime).toISOString());

        const idsToCheck = this.emails.filter(e => {
          if (!e.preview_img_url) {
             // Strict check: must have modified_at
             return !!e['modified_at'];
          }
          if (e['modified_at']) {
            const modTime = new Date(e['modified_at']).getTime();
            return modTime > lastCheckTime;
          }
          return false;
        }).map(e => e.id);
        
        console.log('FolderView Candidate IDs:', idsToCheck);

        if (idsToCheck.length > 0) {
          idsToCheck.forEach(id => this.loadingPreviews.add(id));
          
          this.folderService.checkPreviews(idsToCheck).subscribe({
            next: (res: any) => {
              console.log('FolderView Stream result:', res);
              if (res.error) {
                console.error('Error for email', res.id, res.error);
                this.loadingPreviews.delete(res.id);
                return;
              }

              const email = this.emails.find(e => e.id === res.id);
              if (email && res.preview_img_url) {
                // Update URL and append a timestamp to bust the browser cache
                email.preview_img_url = `${res.preview_img_url}?t=${Date.now()}`;
              }
              this.loadingPreviews.delete(res.id);
            },
            error: (err) => {
              console.error('Error checking previews:', err);
              idsToCheck.forEach(id => this.loadingPreviews.delete(id));
            },
            complete: () => {
              // Update last check time
              localStorage.setItem('last_preview_check_time', Date.now().toString());
            }
          });
        }
        
      },
      error: (err) => {
        console.error('Error loading folder contents:', err);
        this.loading = false;
        this.router.navigate(['/pages/generator']);
      }
    });
  }

  readonly showDynamic$: Observable<boolean> = this.userStore
    .selectEnterprise()
    .pipe(map((enterprise: Enterprise | null) => !!enterprise?.dynamic_html));

  readonly showInter$: Observable<boolean> = this.userStore
    .selectEnterprise()
    .pipe(map((enterprise: Enterprise | null) => !!enterprise?.url_inter?.trim()));
  
  // Search and filtering
  onSearchChange(term: string): void {
    this.searchTerm$.next(term);
    this.filterEmails();
    this.filterFolders();
  }
  
  filterEmails(): void {
    const term = this.searchTerm.toLowerCase();
    if (!term) {
      this.filteredEmails = [...this.emails];
    } else {
      this.filteredEmails = this.emails.filter(email => 
        email.name?.toLowerCase().includes(term)
      );
    }
    this.sortEmails();
  }

  get isSearching(): boolean {
    return !!this.searchTerm.trim();
  }

  getFolderName(email: Email): string {
    if (!email.folder_id) return 'Emails';
    return this.folderNameMap.get(email.folder_id) ?? '';
  }

  getFolderPath(email: Email): string {
    if (!email.folder_id) return 'Emails';
    return this.folderPathMap.get(email.folder_id) ?? '';
  }

  private loadFolderMaps(): void {
    if (this.folderMapLoaded) return;
    this.folderMapLoaded = true;
    this.folderService.getAllFolders().subscribe({
      next: (response) => {
        const byId = new Map<number, EmailFolder>();
        response.folders.forEach(folder => byId.set(folder.id, folder));

        this.folderNameMap.clear();
        this.folderPathMap.clear();
        response.folders.forEach(folder => {
          const names: string[] = [];
          const seen = new Set<number>();
          let node: EmailFolder | undefined = folder;
          while (node && !seen.has(node.id)) {
            seen.add(node.id);
            names.unshift(node.name);
            node = node.parent_id !== null ? byId.get(node.parent_id) : undefined;
          }
          this.folderNameMap.set(folder.id, folder.name);
          this.folderPathMap.set(folder.id, ['Emails', ...names].join(' / '));
        });
      },
      error: (err) => {
        this.folderMapLoaded = false;
        console.error('Error loading folder name map:', err);
      }
    });
  }

  private performGlobalSearch(): void {
    if (!this.isSearching) {
      this.otherEmails = [];
      return;
    }
    this.loadFolderMaps();
    if (this.allEmailsCache) {
      this.applyGlobalFilter();
      return;
    }
    // A response started before the cache was invalidated must be ignored
    const version = ++this.globalSearchVersion;
    this.loadingGlobalSearch = true;
    this.generatorService.getEmails().subscribe({
      next: (emails) => {
        if (version !== this.globalSearchVersion) return;
        this.allEmailsCache = emails;
        this.loadingGlobalSearch = false;
        this.applyGlobalFilter();
      },
      error: (err) => {
        if (version !== this.globalSearchVersion) return;
        console.error('Error loading all emails:', err);
        this.loadingGlobalSearch = false;
      }
    });
  }

  private applyGlobalFilter(): void {
    if (!this.allEmailsCache || !this.isSearching) {
      this.otherEmails = [];
      return;
    }
    const term = this.searchTerm.toLowerCase();
    const localIds = new Set(this.emails.map(e => e.id));
    this.otherEmails = this.allEmailsCache.filter(email =>
      !localIds.has(email.id) && email.name?.toLowerCase().includes(term)
    );
    this.sortEmailList(this.otherEmails);
  }

  private filterFolders(): void {
    const term = this.searchTerm.toLowerCase();
    if (!term) {
      this.filteredFolders = [...this.folders];
    } else {
      this.filteredFolders = this.folders.filter(f =>
        f.name?.toLowerCase().includes(term)
      );
    }
    this.sortFolders();
  }
  
  // Sorting
  sortEmails(): void {
    this.sortEmailList(this.filteredEmails);
    this.sortEmailList(this.otherEmails);
  }

  private sortEmailList(list: Email[]): void {
    const sorting = this.sorting$.getValue();
    if (sorting.key) {
      list.sort((a: Email, b: Email) => {
        const valueA = a[sorting.key as keyof Email];
        const valueB = b[sorting.key as keyof Email];
        
        if (valueA !== undefined && valueB !== undefined) {
          return sorting.direction === 'asc' 
            ? valueA < valueB ? -1 : 1
            : valueA < valueB ? 1 : -1;
        }
        return 0;
      });
    }
  }

  private sortFolders(): void {
    const dir = this.sorting$.getValue().direction;
    this.filteredFolders.sort((a, b) => {
      const aTime = new Date(a.created_at).getTime();
      const bTime = new Date(b.created_at).getTime();
      return dir === 'asc' ? aTime - bTime : bTime - aTime;
    });
  }
  
  onSort(): void {
    const currentSorting = this.sorting$.getValue();
    this.sorting$.next({
      key: currentSorting.key,
      direction: currentSorting.direction === 'asc' ? 'desc' : 'asc'
    });
    this.sortEmails();
    this.sortFolders();
  }
  
  get sortDirection(): string {
    return this.sorting$.getValue().direction || 'desc';
  }

  // Menu handling
  toggleEmailMenu(event: MouseEvent, email: Email): void {
    event.stopPropagation();
  
    if (this.showMenu && this.currentEmail === email) {
      this.showMenu = false;
      this.currentEmail = null;
    } else {
      this.currentEmail = email;
      this.showMenu = true;
    }
  }
  
  toggleFolderMenu(event: MouseEvent, folder: EmailFolder): void {
    event.stopPropagation();

    this.showMenu = false;
    
    
    if (this.showFolderMenu && this.currentFolder === folder) {
      this.showFolderMenu = false;
      this.currentFolder = null;
    } else {
      this.showFolderMenu = true;
      this.currentFolder = folder;
    }
  }
  
  closeMenu(event: MouseEvent): void {
    // Only close menus if we're not clicking on a menu toggle button
    if (!(event.target as HTMLElement).closest('.menu-toggle')) {
      this.showMenu = false;
      this.showFolderMenu = false;
    }
  }
  
  confirmDeleteEmail(email: Email): void {
    this.currentEmail = email;
    this.dialogService.open(this.deleteEmailDialog);
  }
  
  public deleteEmail(email: Email, dialogRef:any): void {
    // Subscribe to the delete completion event
    const subscription = this.emailStore.deleteEmailCompleted.subscribe(() => {
      // Remove the email from our local arrays
      this.emails = this.emails.filter(e => e.id !== email.id);
      this.allEmailsCache = this.allEmailsCache?.filter(e => e.id !== email.id) ?? null;
      this.otherEmails = this.otherEmails.filter(e => e.id !== email.id);
      this.filterEmails(); // Re-apply filters and sorting
      subscription.unsubscribe();
    });
    
    // Trigger the delete operation
    this.emailStore.deleteEmail({ id: email.id });
    dialogRef.close();
  }
  
  duplicateEmail(email: Email): void {
    this.loadingToast = this.toastService.loading(
      'Duplication en cours',
      `L'email "${email.name}" est en train d'être dupliqué`
    );
    // Subscribe to the copy completion event
    const subscription = this.emailStore.copyEmailCompleted.subscribe(() => {
      if (this.loadingToast) {
        this.toastService.hideLoading(this.loadingToast);
        this.loadingToast = undefined;
      }
      // Load the updated list of emails
      this.loadFolderContents();
      subscription.unsubscribe();
    });

    // Trigger the copy operation
    this.emailStore.copyEmail({ id: email.id, name: email.name + ' (copie)' });
  }

  public openDynamicChoiceDialog(email: Email): void {
    this.dialogService.open(this.dialogChoiceTemplate, {
      context: { email }
    });
  }

  public onDynamicTypeSelected(ref: any, email: Email, useCategory: boolean): void {
    ref.close(); // Fermer la popin

    const subscription = this.emailStore.copyEmailCompleted.subscribe(() => {
      subscription.unsubscribe();
    });

    this.emailStore.createDynamicEmail({
      id: email.id,
      name: email.name + " (dynamic)",
      useCategory: useCategory
    });
  }
  
  // Translation functionality
  getLangageCode(dialog: TemplateRef<any>): void {
    this.generatorService.getLangagecode(1).subscribe((data: any) => {
      this.languageList = Object.entries(data).map(([code, text]) => ({ code, text: text as string }));
      this.dialogService.open(dialog, {
        context: 'Choisissez une langue:',
        hasBackdrop: true,
      });
    });
  }
  
  selectLanguage(email: Email, langCode: string, dialogRef: any): void {
    dialogRef.close();
    const subscription = this.emailStore.translateEmailCompleted.subscribe(() => {
      this.loadFolderContents();
      subscription.unsubscribe();
    });
    // Trigger the translation operation
    this.emailStore.translateEmail({ id: email.id, langage_code: langCode });
  }

  createInterEmail(email: Email): void {
    const subscription = this.emailStore.interEmailCompleted.subscribe(() => {
      this.loadFolderContents();
      subscription.unsubscribe();
    });
    this.emailStore.createInterEmail({ id: email.id });
  }

  /** returns [folderId, ...all its descendants' ids] */
  private getInvalidDestinationIds(
    folders: EmailFolder[],
    folderToMoveId: number
  ): number[] {
    let ids: number[] = [];

    // find the node in the tree
    const findNode = (items: EmailFolder[]): EmailFolder | undefined => {
      for (const f of items) {
        if (f.id === folderToMoveId) return f;
        if (f.children) {
          const found = findNode(f.children);
          if (found) return found;
        }
      }
      return undefined;
    };

    const node = findNode(folders);
    if (!node) return [];

    // collect its id + all children recursively
    const collect = (f: EmailFolder) => {
      ids.push(f.id);
      if (f.children) f.children.forEach(collect);
    };
    collect(node);
    return ids;
  }

  // Folder operations
  openCreateFolderDialog(dialog: TemplateRef<any>) {
    this.newFolderName = 'Nouveau dossier';
    this.selectedFolder = null;
    this.dialogService.open(dialog, {
      context: 'Create new folder'
    });
  }

  createFolder(dialogRef: NbDialogRef<any>) {
    if (this.newFolderName.trim()) {
      this.folderService.createFolder(this.newFolderName, this.folderId).subscribe({
        next: () => {
          this.loadFolderContents();
          dialogRef.close();
        },
        error: (err) => console.error('Error creating folder:', err)
      });
    }
  }

  openEditFolderDialog(folder: EmailFolder, dialog: TemplateRef<any>) {
    this.selectedFolder = folder;
    this.newFolderName = folder.name;
    this.dialogService.open(dialog, {
      context: 'Edit folder name'
    });
  }

  updateFolder(dialogRef: NbDialogRef<any>) {
    if (this.selectedFolder && this.newFolderName.trim()) {
      this.folderService.updateFolder(this.selectedFolder.id, { name: this.newFolderName, parent_id: this.selectedFolder.parent_id }).subscribe({
        next: () => {
          this.loadFolderContents();
          dialogRef.close();
        },
        error: (err) => console.error('Error updating folder:', err)
      });
    }
  }

  confirmDeleteFolder(folder: EmailFolder, dialog: TemplateRef<any>) {
    this.selectedFolder = folder;
    this.dialogService.open(dialog, {
      context: 'Are you sure you want to delete this folder?'
    });
  }

  deleteFolder(dialogRef: NbDialogRef<any>) {
    if (this.selectedFolder) {
      this.folderService.deleteFolder(this.selectedFolder.id).subscribe({
        next: (response) => {
          this.loadFolderContents();
          this.successMessage = response.message;
          this.dialogService.open(this.dialogSuccessDeletingFolder);
          dialogRef.close();
        },
        error: (err) => {
          console.error('Error deleting folder:', err);
          // Optionally, you can show an error message to the user
          this.errorMessage = 'Erreur lors de la suppression du dossier : ' + err.error.error;
          this.dialogService.open(this.dialogErrorDeletingFolder);
        }
      });
    }
  }
  
  duplicateFolder(folder: EmailFolder) {
    const newName = `${folder.name} (copy)`;
    this.folderService.createFolder(newName, this.folderId).subscribe({
      next: () => {
        this.loadFolderContents();
        this.showFolderMenu = false;
      },
      error: (err) => console.error('Error duplicating folder:', err)
    });
  }

  // Move folder dialog
  openMoveFolderDialog(folder: EmailFolder, dialog: TemplateRef<any>) {
    this.selectedFolder = folder;

    // Reload tree data, then…
    this.loadAllFolders(() => {
      // 1) set the initial “parent” as default
      this.destinationFolderId = folder.parent_id;

      // 2) compute the “taboo” list so sub-folders can't be picked
      //    (uses the freshly loaded `allFolders`)
      this.invalidDestinationIds = this.getInvalidDestinationIds(
        this.allFolders,
        folder.id
      );

      // 3) now open the dialog
      this.dialogService.open(dialog, {
        context: 'Select destination folder',
      });
    });
  }

  private loadAllFolders(callback?: () => void): void {
    this.folderService.getAllFolders().subscribe({
      next: (response) => {
        
        // Build hierarchical tree from flat list
        const folderTree = this.buildFolderTree(response.folders);
        
        this.allFolders = folderTree;
        if (callback) callback();
      },
      error: (err) => console.error('Error loading all folders:', err)
    });
  }


  // Move folder
  moveFolder(parentId: number | null, dialogRef: NbDialogRef<any>) {
    if (!this.selectedFolder || parentId === this.selectedFolder.id) {
      return;
    }
    if (this.selectedFolder) {
      // Check to prevent circular references
      if (parentId !== this.selectedFolder.id) {
        dialogRef.close();
        this.loadingToast = this.toastService.loading(
          'Déplacement en cours',
          `L'objet sélectionné est en train d'être déplacé`
        );
        this.folderService.updateFolder(this.selectedFolder.id, { parent_id: parentId }).subscribe({
          next: () => {
            this.toastService.hideLoading(this.loadingToast!);
            this.toastService.success(
              'Déplacement réussi',
              `L'objet sélectionné a bien été déplacé`
            );
            this.loadFolderContents();
            this.loadingToast = undefined;
          },
          error: (err) => {
            // always hide loading if error
            this.toastService.hideLoading(this.loadingToast!);
            this.loadingToast = undefined;

            // optionally show error toast
            this.toastService.toasts.push({
              title: 'Erreur',
              message: `Impossible de déplacer l'objet`,
              status: 'error',
              autohide: true,
            });

            console.error('Error moving folder:', err);
          },
        });
      }
    }
  }

  // Email operations
  openMoveEmailDialog(email: Email, dialog: TemplateRef<any>) {
    this.currentEmail = email;
    // Load all folders for the tree view
    this.loadAllFolders(() => {
      this.destinationFolderId = email.folder_id || null;
      this.dialogService.open(dialog, {
        context: 'Select destination folder'
      });
    });
  }

  moveEmailToFolder(folderId: number | null, dialogRef: NbDialogRef<any>) {
    if (!this.currentEmail) return;
    if (this.currentEmail) {
      dialogRef.close();
      this.loadingToast = this.toastService.loading(
        'Déplacement en cours',
        `L'objet sélectionné est en train d'être déplacé`
      );
      this.folderService.moveEmailToFolder(this.currentEmail.id, folderId, this.currentEmail.name, true).subscribe({
        next: () => {
          this.toastService.hideLoading(this.loadingToast!);
          this.toastService.success(
            'Déplacement réussi',
            `L'objet sélectionné a bien été déplacé`
          );
          this.loadingToast = undefined;
          // Refresh the folder contents
          this.loadFolderContents();
        },
        error: (err) => {
          this.toastService.hideLoading(this.loadingToast!);
          this.loadingToast = undefined;
          this.toastService.toasts.push({
            title: 'Erreur',
            message: `Impossible de déplacer l'objet`,
            status: 'error',
            autohide: true,
          });
          console.error('Error moving email:', err);
        },
      });
    }
  }

  // Navigation
  navigateToFolder(folderId: number) {
    this.router.navigate(['/pages/generator/folder', folderId]);
  }

  navigateToBreadcrumb(item: any) {
    if (item.id === 'root') {
      this.router.navigate(['/pages/generator']);
    } else {
      this.router.navigate(['/pages/generator/folder', item.id]);
    }
  }

  // Navigate to email editor
  editEmail(email: Email) {
    const previousUrl = this.location.path(true);
    localStorage.setItem('editor_previous_url', previousUrl);
  
    this.router.navigate(['/pages/generator-v2', email.id]);
  }

  // Display mode toggle
  onDisplayModeChange() {
    sessionStorage.setItem('displayMode', this.displayMode);
  }

  private buildFolderTree(flatFolders: EmailFolder[]): EmailFolder[] {
    // Create a dictionary for quick lookups with folder IDs as keys
    const folderMap: { [key: number]: EmailFolder & { children: EmailFolder[] } } = {};
    
    // Initialize the children array for each folder
    flatFolders.forEach(folder => {
      folderMap[folder.id] = { ...folder, children: [] };
    });
    
    // Organize folders into a hierarchical structure
    const rootFolders: EmailFolder[] = [];
    
    Object.values(folderMap).forEach(folder => {
      if (folder.parent_id === null) {
        // This is a root-level folder
        rootFolders.push(folder);
      } else {
        // This is a child folder, add it to its parent's children array
        const parent = folderMap[folder.parent_id];
        if (parent) {
          parent.children.push(folder);
        } else {
          // If parent doesn't exist, treat as root folder
          rootFolders.push(folder);
        }
      }
    });
    
    return rootFolders;
  }

  onDragStart(event: DragEvent, email: Email) {
    const sourceEl = event.currentTarget as HTMLElement;
    sourceEl.classList.add('dragging');

    event.dataTransfer!.setData('application/json', JSON.stringify({ id: email.id, name: email.name }));
    event.dataTransfer!.effectAllowed = 'move';

    const clone = sourceEl.cloneNode(true) as HTMLElement;
    clone.style.position       = 'absolute';
    clone.style.top            = '-1000px';
    clone.style.left           = '-1000px';
    clone.style.pointerEvents  = 'none';
    clone.style.opacity        = '1';
    clone.style.width          = `200px`;
    document.body.appendChild(clone);

    const rect = sourceEl.getBoundingClientRect();
    event.dataTransfer!.setDragImage(clone, rect.width/2, rect.height/2);

    // remove clone after drag image is captured
    setTimeout(() => document.body.removeChild(clone), 0);
  }

  onDragEnd(event: DragEvent) {
    const sourceEl = event.currentTarget as HTMLElement;
    sourceEl.classList.remove('dragging');
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    const el = event.currentTarget as HTMLElement;
    if (!el.classList.contains('drag-over')) {
      el.classList.add('drag-over');
    }
    event.dataTransfer!.dropEffect = 'move';
  }

  onDragEnter(event: DragEvent) {
    (event.currentTarget as HTMLElement).classList.add('drag-over');
  }
  onDragLeave(event: DragEvent) {
    const el = event.currentTarget as HTMLElement;
    const { left, right, top, bottom } = el.getBoundingClientRect();
    const x = event.clientX, y = event.clientY;

    // if pointer is still inside the bounds, do nothing
    if (x >= left && x <= right && y >= top && y <= bottom) {
      return;
    }
    el.classList.remove('drag-over');
  }

  onDrop(event: DragEvent, folder: EmailFolder) {
    event.preventDefault();
    const el = event.currentTarget as HTMLElement;
    el.classList.remove('drag-over');

    const data = event.dataTransfer?.getData('application/json');
    if (!data) return;
    const { id, name } = JSON.parse(data);

    this.loadingToast = this.toastService.loading(
      'Déplacement en cours',
      `L'objet sélectionné est en train d'être déplacé`
    );

    this.folderService
      .moveEmailToFolder(id, folder.id, name, true)
      .subscribe({
        next: () => {
          this.toastService.hideLoading(this.loadingToast!);
          this.toastService.success(
            'Déplacement réussi',
            `L'objet sélectionné a bien été déplacé`
          );
          this.loadingToast = undefined;
          // Refresh the folder contents
          this.loadFolderContents();
        },
        error: (err) => {
          this.toastService.hideLoading(this.loadingToast!);
          this.loadingToast = undefined;
          this.toastService.toasts.push({
            title: 'Erreur',
            message: `Impossible de déplacer l'objet`,
            status: 'error',
            autohide: true,
          });
          console.error('Error moving email:', err);
        },
      });
  }

}